import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { Label } from '@ipfabric/ui-components/Label/Label';
import { FloatingList } from '@ipfabric/ui-components/List/FloatingList/FloatingList';
import type { FloatingListProps } from '@ipfabric/ui-components/List/FloatingList/FloatingList.types';
import { Switch } from '@ipfabric/ui-components/Switch/Switch';
import { prop } from '@ipfabric/utils/fp/prop';
import {
  isTableActionDropdownMenuItem,
  isTableStandaloneActionItem,
  useTableContextSelectors,
} from 'components/Table/model/TableModel';
import { setAutoSizedColumns } from 'components/Table/model/commands/setAutoSizedColumns';
import { selectAutoSizedColumns } from 'components/Table/model/selectors/selectAutoSizedColumns';
import { useLocale } from 'containers/App/LocaleContext';
import { memo, useMemo } from 'react';
import type { FC } from 'react';
import { changeTableSettingsVisibility } from '../model/commands/changeTableSettingsVisibility';
import { restoreTableSettings } from '../model/commands/restoreTableSettings';

const BareTableActions: FC = () => {
  const actionsMsg = useLocale().table.actions;
  const ctxProps = useTableContextSelectors({
    dispatch: prop('dispatch'),
    loadingSettings: prop('loadingSettings'),
    menuItems: prop('menuItems'),
    autoSizedColumns: selectAutoSizedColumns,
    disableRestoreSettings: prop('disableRestoreSettings'),
    disableContextMenu: prop('disableContextMenu'),
    hideAutoSizing: prop('hideAutoSizing'),
    tableContainerRef: prop('tableContainerRef'),
  });

  const overflowMenuItems: FloatingListProps['items'] = useMemo(
    () => [
      {
        label: 'Display settings',
        disabled: ctxProps.loadingSettings === 'LOADING',
        startAdornment: <Icon glyph='fa-sliders' iconStyle='solid' />,
        onClick: () => {
          ctxProps.dispatch(changeTableSettingsVisibility, { value: true });
        },
      },
      ...(!ctxProps.disableRestoreSettings
        ? [
            {
              label: 'Restore table settings',
              startAdornment: (
                <Icon glyph='fa-rotate-left' iconStyle='regular' />
              ),
              onClick: () => {
                ctxProps.dispatch(restoreTableSettings);
              },
            },
          ]
        : []),
      ...(ctxProps.menuItems?.filter(isTableActionDropdownMenuItem) ?? []),
    ],
    [
      ctxProps.menuItems,
      ctxProps.loadingSettings,
      ctxProps.dispatch,
      ctxProps.disableRestoreSettings,
    ],
  );

  const autoSizeSwitchId = useHtmlId('autoSizeColumnsSwitch');

  return (
    <div className='flex flex-row items-center gap-xs'>
      {!ctxProps.hideAutoSizing && (
        <div className='flex flex-row gap-3xs'>
          <Switch
            size='small'
            id={autoSizeSwitchId}
            onChange={(value) => {
              ctxProps.dispatch(setAutoSizedColumns, { value });
            }}
            value={ctxProps.autoSizedColumns}
          />
          <Label
            size='small'
            htmlFor={autoSizeSwitchId}
            tooltipContent={actionsMsg.autoSizeColumnsTooltip}
          >
            {actionsMsg.autoSizeColumns}
          </Label>
        </div>
      )}
      {ctxProps.menuItems
        ?.filter(isTableStandaloneActionItem)
        .map(prop('elementWithKey'))}
      {!ctxProps.disableContextMenu && (
        <FloatingList items={overflowMenuItems}>
          {(setRef, setIsOpen, open) => (
            <IconButton
              variant='outline'
              size='small'
              type='button'
              icon='fa-ellipsis'
              onClick={() => {
                setIsOpen(!open);
              }}
              forwardRef={setRef}
              iconProps={{
                className: 'p-0 m-0',
              }}
            />
          )}
        </FloatingList>
      )}
    </div>
  );
};

export const TableActions = memo(BareTableActions);
